import { HttpInterceptor, HttpRequest, HttpHandler, HttpErrorResponse, HTTP_INTERCEPTORS } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, throwError, switchMap, filter, take, retryWhen, mergeMap, timer } from 'rxjs';
import { TokenStorageService } from './token-storage.service';
import { UserService } from './user.service';
import { AzureTokenService } from './azure-token.service';
import { environment } from 'src/environments/environment';

const TOKEN_HEADER_KEY = 'Authorization';
const TOKEN_TYPE_PROVIDER = 'Bearer';

@Injectable()

export class HttpAuthInterceptorService implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(private tokenService: TokenStorageService, private userService: UserService, private azureTokenService: AzureTokenService) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<any> {
    let authReq = req;

    // Handle Azure Management API calls separately
    let isAzureApiUrl = false;
    try {
      const parsedUrl = new URL(req.url, window.location.origin);
      isAzureApiUrl = parsedUrl.hostname.toLowerCase() === 'management.azure.com';
    } catch {
      isAzureApiUrl = false;
    }
    if (isAzureApiUrl) {
      if (this.azureTokenService.isLoggedIn()) {
        const azureToken = this.azureTokenService.retrieveToken();
        if (azureToken?.accessToken) {
          authReq = req.clone({
            setHeaders: { Authorization: `Bearer ${azureToken.accessToken}` }
          });
        }
      }
      return next.handle(authReq).pipe(catchError(error => {
        return throwError(() => error);
      }));
    }

    const token = this.tokenService.getToken();
    const auth0Token = this.tokenService.getAuth0Token();
    
    // Determine which token to use
    let tokenToUse: string | null = null;
    
    // For gateway API calls, prefer auth0 token (fallback to regular token if needed)
    if (authReq.url.includes('private_gateway')) {
      tokenToUse = auth0Token ?? token;
    } else {
      // For other calls: If QA env then pass auth0 token in header else pass regular token in header
      let isQAEnvironment = false;
      try {
        const baseUrl = new URL(environment.baseURL);
        isQAEnvironment = baseUrl.hostname === 'api.qa.eprimerx.com';
      } catch {
        isQAEnvironment = false;
      }
      tokenToUse = (isQAEnvironment && auth0Token) ? auth0Token : token;
    }
    
    if (tokenToUse != null) {


      // if(!authReq.url.includes('oauth/token') && !authReq.url.includes('ProviderConfiguration') && !authReq.url.includes('refresh-token') && !authReq.url.includes('GetPharmacy'))
      if(!authReq.url.includes('oauth/token') && !authReq.url.includes('primecomm') && !authReq.url.includes('refresh-token'))
      {
        authReq = this.addTokenHeader(req, tokenToUse);
      }
    }
    return next.handle(authReq).pipe(catchError(error => {
      if (error instanceof HttpErrorResponse && !authReq.url.includes('/storeConfig/auth/user/AuthenticateByPseudoName') && !authReq.url.includes('/User/Authenticate') && error.status === 401) {
        return this.handle401Error(authReq, next);
      }
      return throwError(()=>error);
    }));
  }


  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);
      const token = this.tokenService.getRefreshToken();
      if (token)
        //Add ref token api call here
        return this.userService.refreshToken(token).pipe(
          switchMap((token: any) => {
            this.isRefreshing = false;
            this.tokenService.saveToken(token.token);
            this.refreshTokenSubject.next(token.refreshToken);

            return next.handle(this.addTokenHeader(request, token.accessToken));
          }),
          catchError((err) => {
            this.isRefreshing = false;

            this.tokenService.signOut();
            return throwError(()=>err);
          })
        );
    }
    return this.refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap((token) => next.handle(this.addTokenHeader(request, token)))
    );
  }
  private addTokenHeader(request: HttpRequest<any>, token: string) {
    /* for Spring Boot back-end */
    // return request.clone({ headers: request.headers.set(TOKEN_HEADER_KEY, 'Bearer ' + token) });
    /* for Node.js Express back-end */
    let tokenHeader = TOKEN_TYPE_PROVIDER + " " + token;
    return request.clone({
      headers: request.headers.set(TOKEN_HEADER_KEY, tokenHeader)
    });
  }
}


export const authInterceptorProviders = [
  { provide: HTTP_INTERCEPTORS, useClass: HttpAuthInterceptorService, multi: true }
];
